import { CollapsibleFilters } from '../../common/CollapsibleFilters';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, Card, CardContent, Grid, CircularProgress, Alert, Button, Stack, FormControl, FormLabel, Input, Select, Option } from '@mui/joy';
import { TrendUp as TrendingUpIcon, Star as StarIcon, Timer as TimerIcon } from '@phosphor-icons/react';
import { Bar, BarChart, Line, LineChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { api } from '../../../lib/api';

interface ReviewStats {
  total_reviews: number;
  published_replies: number;
  managed_reviews: number;
  unanswered: number;
  publication_failures: number;
  median_reply_hours: number | null;
  month_change: number;
  email_outcomes: Record<string, number>;
  attribution_note: string;
  average_rating: number;
  reviews_by_platform: Record<string, number>;
  pending_requests: number;
  response_rate: number;
  recent_reviews: any[];
  rating_distribution: Array<{ rating: number; count: number }>;
  review_velocity: Array<{ month: string; count: number }>;
}

export const ReviewReports: React.FC = () => {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [platform, setPlatform] = useState('');
  const [campaign, setCampaign] = useState('');
  const { data: options } = useQuery({ queryKey: ['review-report-options'], queryFn: async () =>
    (await api.get<{ platforms: { id: number; name: string }[]; campaigns: { id: number; name: string }[] }>('/api/marketing/review-requests/report-options/')).data });
  // Fetch review stats
  const { data: stats, isLoading, error } = useQuery<ReviewStats>({
    queryKey: ['review-stats', 'report', start, end, platform, campaign],
    queryFn: async () => {
      const response = await api.get<ReviewStats>(`/api/marketing/reviews/stats/?${new URLSearchParams({ ...(start ? { start } : {}), ...(end ? { end } : {}), ...(platform ? { platform } : {}), ...(campaign ? { campaign } : {}) })}`);
      return response.data;
    },
  });

  // Rating distribution from real data
  const ratingDistributionData = stats?.rating_distribution
    ? [5, 4, 3, 2, 1].map(rating => {
        const item = stats.rating_distribution.find(r => r.rating === rating);
        return {
          rating: `${rating}★`,
          count: item?.count || 0
        };
      })
    : [];

  // Review velocity from real data
  const reviewVelocityData = stats?.review_velocity
    ? stats.review_velocity.map(item => ({
        month: new Date(`${item.month.slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short' }),
        reviews: item.count
      }))
    : [];

  const platformData = stats?.reviews_by_platform ?
    Object.entries(stats.reviews_by_platform).map(([name, value]) => ({
      name,
      value: value as number
    })) : [];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  return (
    <Box>
      <Typography level="h4" sx={{ mb: 3 }}>Review Analytics</Typography>

      <CollapsibleFilters><Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <FormControl><FormLabel>From</FormLabel><Input type="date" value={start} onChange={e => setStart(e.target.value)} /></FormControl>
        <FormControl><FormLabel>Through</FormLabel><Input type="date" value={end} onChange={e => setEnd(e.target.value)} /></FormControl>
        <FormControl><FormLabel>Connected location</FormLabel><Select value={platform} onChange={(_, value) => setPlatform(value || '')}><Option value="">All locations and imports</Option>{options?.platforms.map(item => <Option key={item.id} value={String(item.id)}>{item.name}</Option>)}</Select></FormControl>
        <FormControl sx={{ maxWidth: '100%' }}><FormLabel>Recent recipient campaigns</FormLabel><Select value={campaign} onChange={(_, value) => setCampaign(value || '')}><Option value="">All campaigns</Option>{options?.campaigns.map(item => <Option key={item.id} value={String(item.id)}>{item.name}</Option>)}</Select></FormControl>
        <Button variant="plain" onClick={() => { setStart(''); setEnd(''); setPlatform(''); setCampaign(''); }}>Reset filters</Button>
      </Stack></CollapsibleFilters>
      {isLoading && <CircularProgress aria-label="Loading report" />}
      {error && <Alert color="danger">Unable to load this report. Check the date range and try again.</Alert>}
      {stats && <>
      <Alert color="neutral" sx={{ mb: 2 }}>{stats.attribution_note}</Alert>
      <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
        {Object.entries(stats.email_outcomes).map(([name, value]) => <Card key={name} size="sm" sx={{ minWidth: 115 }}><Typography level="body-sm">{{ sent: 'Sent', delivered: 'Delivered', opened: 'Opened', clicked: 'Visited link', failed: 'Delivery issues', queued: 'Queued / paused', linked_reviews: 'Linked reviews' }[name] || name}</Typography><Typography level="h3">{value}</Typography></Card>)}
      </Stack>
      <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
        <Card><Typography>Unanswered connected reviews</Typography><Typography level="h3">{stats.unanswered}</Typography><Button component="a" href="/marketing/reviews?tab=reviews&inbox=unanswered" variant="outlined">Open unanswered inbox</Button></Card>
        <Card><Typography>Publication failures</Typography><Typography level="h3">{stats.publication_failures}</Typography><Button component="a" href="/marketing/reviews?tab=reviews&inbox=failed" variant="outlined">Retry failed replies</Button></Card>
        <Card><Typography>Median recorded reply delay</Typography><Typography level="h3">{stats.median_reply_hours === null ? 'No data' : `${stats.median_reply_hours} hours`}</Typography></Card>
        <Card><Typography>This month vs last month</Typography><Typography level="h3">{stats.month_change > 0 ? '+' : ''}{stats.month_change} reviews</Typography><Typography level="body-xs">Within the selected filters; current month is partial.</Typography></Card>
      </Stack>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography level="body-sm">Total Reviews</Typography>
              <Typography level="h3">{stats?.total_reviews || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography level="body-sm">Average Rating</Typography>
              <Typography level="h3">
                {stats?.average_rating?.toFixed(1) || '0.0'}★
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography level="body-sm">Published reply rate · connected reviews</Typography>
              <Typography level="h3">{stats?.response_rate?.toFixed(0) || 0}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography level="body-sm">Pending Requests</Typography>
              <Typography level="h3">{stats?.pending_requests || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUpIcon />
                <Typography level="title-lg">Review Velocity</Typography>
              </Box>
              {reviewVelocityData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={reviewVelocityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="reviews"
                      stroke="#0B6BCB"
                      strokeWidth={2}
                      dot={{ fill: '#0B6BCB' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                  No review data available for the last 6 months
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <StarIcon />
                <Typography level="title-lg">Rating Distribution</Typography>
              </Box>
              {ratingDistributionData.some(d => d.count > 0) ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={ratingDistributionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="rating" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${value} reviews`, 'Count']} />
                    <Bar dataKey="count" fill="#0B6BCB" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                  No reviews to display
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TimerIcon />
                <Typography level="title-lg">Reviews by Platform</Typography>
              </Box>
              {platformData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={platformData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {platformData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                  No platform data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>Recent Reviews</Typography>
              {stats?.recent_reviews && stats.recent_reviews.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {stats.recent_reviews.slice(0, 3).map((review) => (
                    <Box key={review.id} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography level="body-sm" fontWeight="lg">
                          {review.reviewer_name}
                        </Typography>
                        <Typography level="body-sm">
                          {'★'.repeat(review.rating)}
                        </Typography>
                      </Box>
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {review.review_text?.substring(0, 100)}...
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  No recent reviews
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </>}
    </Box>
  );
};
